import { useState, useCallback, useMemo, useEffect } from "react";
import { useHistory } from "react-router-dom";
import styles from "./MyCourses.module.scss";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  SllCourse,
  SllLesson,
  useSllGetGettingStartedLessonsLazyQuery,
  useSllGetBrainBreakLessonsLazyQuery,
} from "@api/graphql-codegen";
import Spinner from "src/components/spinner/Spinner";
import Header from "src/components/header/Header";
import GridLessonsLayout from "./gridLayout/GridLessonsLayout";
import "./customStyle.css";
import { useQueryParams, useUser } from "@hooks";
import { MyCourseEnum } from "@types";
import TabLayout from "./tabLayout/TabLayout";
import { TouchBackend } from "react-dnd-touch-backend";
import {
  DndProvider,
  TouchTransition,
  MouseTransition,
} from "react-dnd-multi-backend";
import { colors, darkColors } from "./MyCourses";
import LessonProgressModal from "@src/components/lesson-progress-modal/LessonProgressModal";
import {
  auGettingStartedGradeLevels,
  auBrainBreaksGradeLevels,
  gettingStartedGradeLevels,
  GettingStartedMode,
  knownGettingStartedOrder,
} from "@src/constants";
import { isJSON } from "@src/utils/validation";
import { expandGradeLevel } from "@src/utils/helpers";
import FooterPage from "@src/components/footer-page/FooterPage";

const HTML5toTouch = {
  backends: [
    {
      id: "html5",
      backend: HTML5Backend,
      transition: MouseTransition,
    },
    {
      id: "touch",
      backend: TouchBackend,
      options: { enableTouchEvents: true },
      preview: true,
      transition: TouchTransition,
      skipDispatchOnTransition: true,
    },
  ],
};

function checkRepeat(array: SllCourse[], el: string) {
  for (var i = 0; i < array.length; i++) if (array[i].color === el) return true;
  return false;
}

function getRandColor(courses: SllCourse[], array: string[]) {
  var rand = array[Math.floor(Math.random() * array.length)];
  if (!checkRepeat(courses, rand)) {
    return rand;
  }
  return getRandColor(courses, array);
}

function GettingStarted() {
  const history = useHistory();
  const params = useQueryParams();
  const [user] = useUser();
  const tab = params.get("tab");
  const lessonId = params.get("lid");
  const [showLesson, setShowLesson] = useState(false);
  const [selLesson, setSelLesson] = useState<SllLesson | null>(null);
  const [selMyCourse, setSelMyCourse] = useState<SllCourse | undefined>(
    undefined,
  );
  const [topPos, setTopPos] = useState(88);
  const [navHidden, setNavHidden] = useState(false);
  const [myStartedMode, setMyStartedMode] = useState(
    tab === "1" ? GettingStartedMode[0] : GettingStartedMode[1],
  );

  const isAUuser = useMemo(
    () => user?.id?.toLowerCase().startsWith("au"),
    [user?.id],
  );
  const isTablet = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
      userAgent,
    );
  }, []);

  const [getGettingStartedLessons, { data: sllLessons, loading }] =
    useSllGetGettingStartedLessonsLazyQuery({ fetchPolicy: "network-only" });
  const [
    getBrainBreakLessons,
    { data: brainBreaksData, loading: brainBreaksLoading },
  ] = useSllGetBrainBreakLessonsLazyQuery({ fetchPolicy: "network-only" });

  useEffect(() => {
    getGettingStartedLessons();
  }, [getGettingStartedLessons]);

  useEffect(() => {
    getBrainBreakLessons();
  }, [getBrainBreakLessons]);

  useEffect(() => {
    if (tab === "1") {
      setMyStartedMode(GettingStartedMode[0]);
    } else {
      setMyStartedMode(GettingStartedMode[1]);
    }
  }, [tab]);

  const myCourses: SllCourse[] = useMemo(() => {
    const newCourses: SllCourse[] = [];
    if (isAUuser) {
      auGettingStartedGradeLevels.forEach((grade) => {
        const gradeLessons = sllLessons?.sllGetGettingStartedLessons
          ?.filter((lesson) => lesson.gradeLevel === grade)
          .sort((a, b) => {
            if (
              knownGettingStartedOrder.indexOf(a.name.toLowerCase()) > -1 &&
              knownGettingStartedOrder.indexOf(b.name.toLowerCase()) > -1
            )
              return (
                knownGettingStartedOrder.indexOf(b.name.toLowerCase()) -
                knownGettingStartedOrder.indexOf(a.name.toLowerCase())
              );
            else return 0;
          })
          .map((lesson) => {
            return lesson.id;
          });
        if (gradeLessons && gradeLessons?.length > 0) {
          const color = getRandColor(newCourses, colors);
          const foundIdx = colors.findIndex((el) => el === color);
          const darkColor = darkColors[foundIdx];
          newCourses.push({
            name: grade,
            color,
            darkColor,
            id: grade,
            lessonIds: gradeLessons,
          });
        }
      });
    } else {
      gettingStartedGradeLevels.forEach((grade) => {
        const gradeLessons = sllLessons?.sllGetGettingStartedLessons
          ?.filter((lesson) => {
            const expandedGrades = expandGradeLevel(grade);
            if (isJSON(lesson.gradeLevel)) {
              const arrayLevel = JSON.parse(lesson.gradeLevel || "[]");
              return arrayLevel.some((item) => expandedGrades.includes(item));
            } else {
              return lesson.gradeLevel === grade;
            }
          })
          .map((lesson) => {
            return lesson.id;
          });
        if (gradeLessons && gradeLessons?.length > 0) {
          const color = getRandColor(newCourses, colors);
          const foundIdx = colors.findIndex((el) => el === color);
          const darkColor = darkColors[foundIdx];
          newCourses.push({
            name: grade,
            color,
            darkColor,
            id: grade,
            lessonIds: gradeLessons,
          });
        }
      });
    }
    return newCourses;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sllLessons?.sllGetGettingStartedLessons?.length]);

  const brainBreaks: SllCourse[] = useMemo(() => {
    const newCourses: SllCourse[] = [];
    (isAUuser ? auBrainBreaksGradeLevels : gettingStartedGradeLevels).forEach(
      (grade) => {
        const gradeLessons = brainBreaksData?.sllGetBrainBreakLessons
          ?.filter((lesson) => lesson.gradeLevel === grade)
          .map((lesson) => {
            return lesson.id;
          });
        if (gradeLessons && gradeLessons?.length > 0) {
          const color = getRandColor(newCourses, colors);
          const foundIdx = colors.findIndex((el) => el === color);
          const darkColor = darkColors[foundIdx];
          newCourses.push({
            name: grade,
            color,
            darkColor,
            id: grade,
            lessonIds: gradeLessons,
          });
        }
      },
    );
    return newCourses;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brainBreaksData?.sllGetBrainBreakLessons]);

  useEffect(() => {
    if (!lessonId || lessonId === selLesson?.id) return;
    const lesson = sllLessons?.sllGetGettingStartedLessons?.find(
      (l) => l.id && l.id === lessonId,
    ) as SllLesson;
    if (lesson?.id) {
      history.replace(
        `/getting-started?tab=${myStartedMode === GettingStartedMode[0] ? "1" : "2"}` +
          (lesson.id ? `&lid=${lesson?.id}` : ""),
      );
      setSelLesson(lesson);
      setShowLesson(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lessonId,
    selLesson?.id,
    sllLessons?.sllGetGettingStartedLessons?.length,
  ]);

  useEffect(() => {
    if (!selMyCourse) {
      const noLessonCourse = myCourses.find(
        (course) => course.lessonIds?.length === 0,
      );
      setSelMyCourse(noLessonCourse);
    }
  }, [myCourses, selMyCourse, setSelMyCourse]);

  const handleOpenLesson = useCallback(
    (course?: SllCourse) => (lesson: SllLesson) => {
      setSelMyCourse(course);
      if (lesson) {
        setSelLesson(lesson);
        history.replace(
          `/getting-started?tab=${myStartedMode === GettingStartedMode[0] ? "1" : "2"}` +
            (lesson.id ? `&lid=${lesson?.id}` : ""),
        );
        setShowLesson(true);
      }
    },
    [history, myStartedMode],
  );

  const handleSelectPlanMode = useCallback(
    (planMode: any) => {
      setMyStartedMode(planMode);
      if (planMode !== myStartedMode) {
        history.replace(
          `/getting-started?tab=${planMode === GettingStartedMode[0] ? "1" : "2"}` +
            (lessonId ? `&lid=${lessonId}` : ""),
        );
      }
    },
    [history, lessonId, myStartedMode],
  );

  const unitsViewStyle = useMemo(() => {
    const classes = [styles.unitsView, styles.leftPanelHidden];
    return classes.join(" ");
  }, []);

  const brainBreaksView = useCallback(
    (sp: SllCourse) => {
      var lessons: SllLesson[] = [];
      (sp?.lessonIds || []).forEach((lessonId) => {
        if (
          (brainBreaksData?.sllGetBrainBreakLessons?.find(
            (lesson) => lesson.id && lesson.id === lessonId,
          ) as SllLesson) !== undefined
        ) {
          lessons.push(
            brainBreaksData?.sllGetBrainBreakLessons?.find(
              (lesson) => lesson.id && lesson.id === lessonId,
            ) as SllLesson,
          );
        }
      });
      return (
        <div id={sp.id} key={sp.id}>
          <div
            className={`${styles.courseViewHeader} ${isTablet ? styles.headTabletStickyView : styles.headStickyView}`}
          >
            <div className={styles.courseViewTitle}>
              <div
                className={styles.subRoundItem}
                style={{ background: `${sp.darkColor}` }}
              >
                <div className={styles.subItemRoundTitle}>
                  {isAUuser ? sp.name.replace("Grade", "Year") : sp.name}
                </div>
              </div>
            </div>
          </div>
          {lessons.length > 0 ? (
            <GridLessonsLayout
              source={0}
              lessons={lessons.reverse()}
              studyMode={"Student Introduction" as MyCourseEnum}
              handleOpenLesson={handleOpenLesson(sp)}
            />
          ) : null}
        </div>
      );
    },
    [
      handleOpenLesson,
      brainBreaksData?.sllGetBrainBreakLessons,
      isTablet,
      isAUuser,
    ],
  );

  const gettingStartedView = useCallback(
    (sp: SllCourse) => {
      var lessons: SllLesson[] = [];
      (sp?.lessonIds || []).forEach((lessonId) => {
        if (
          (sllLessons?.sllGetGettingStartedLessons?.find(
            (lesson) => lesson.id && lesson.id === lessonId,
          ) as SllLesson) !== undefined
        ) {
          lessons.push(
            sllLessons?.sllGetGettingStartedLessons?.find(
              (lesson) => lesson.id && lesson.id === lessonId,
            ) as SllLesson,
          );
        }
      });
      return (
        <div id={sp.id} key={sp.id}>
          <div
            className={`${styles.courseViewHeader} ${isTablet ? styles.headTabletStickyView : styles.headStickyView}`}
          >
            <div className={styles.courseViewTitle}>
              <div
                className={styles.subRoundItem}
                style={{ background: `${sp.darkColor}` }}
              >
                <div className={styles.subItemRoundTitle}>{sp.name}</div>
              </div>
            </div>
          </div>
          {lessons.length > 0 ? (
            <GridLessonsLayout
              source={0}
              lessons={lessons.reverse()}
              studyMode={"Student Introduction" as MyCourseEnum}
              handleOpenLesson={handleOpenLesson(sp)}
            />
          ) : null}
        </div>
      );
    },
    [handleOpenLesson, isTablet, sllLessons?.sllGetGettingStartedLessons],
  );

  const handleScroll = useCallback(() => {
    const navMenuElement = document.getElementById("navMenuBar");
    const leftScrollView = document.getElementById("leftScrollView");
    if (leftScrollView) {
      const rect = leftScrollView.getBoundingClientRect();
      const navBarRect = navMenuElement?.getBoundingClientRect();
      if (rect.top === 0) {
        setNavHidden(false);
        setTopPos(88);
      } else if (navBarRect && navBarRect.height - (87 - rect.top) < 100) {
        // setNavHidden(true)
        setTopPos(rect.top);
      } else {
        setNavHidden(false);
      }
    }
  }, []);

  // var tab_height = middleWidth ? smallWidth ? 120 : 160 : 203;
  const leftViewStyle = useMemo(() => {
    const classes = [styles.leftView];
    classes.push(styles.slideMove);
    return classes.join(" ");
  }, []);

  const unitListView = (
    <div className={unitsViewStyle}>
      {
        <div
          id={"leftScrollView"}
          className={styles.studyModeBackground}
          style={{ height: "100%" }}
        >
          <TabLayout
            studyMode={myStartedMode}
            isGettingStarted
            topPos={topPos}
            handleSelectPlanMode={handleSelectPlanMode}
          />
          {myStartedMode === GettingStartedMode[0] ? (
            <div style={{ paddingBottom: "100px" }}>
              {myCourses.map((sp) => gettingStartedView(sp))}
            </div>
          ) : (
            <div style={{ paddingBottom: "100px" }}>
              {brainBreaks.map((sp) => brainBreaksView(sp))}
            </div>
          )}
        </div>
      }
    </div>
  );

  const closeModal = () => {
    setShowLesson(false);
    setSelLesson(null);
    history.replace(
      `/getting-started?tab=${myStartedMode === GettingStartedMode[0] ? "1" : "2"}`,
    );
  };

  return (
    <>
      <Header isHidden={navHidden} />
      <div className={styles.wrapper}>
        <DndProvider options={HTML5toTouch}>
          {loading || brainBreaksLoading ? (
            <div className={styles.spinner}>
              <Spinner />
            </div>
          ) : (
            <>
              <div id="customunits" className={styles.page}>
                <div className={leftViewStyle} onScroll={handleScroll}>
                  {unitListView}
                </div>

                <div className={styles.gradientView}></div>
              </div>
            </>
          )}
        </DndProvider>
        <FooterPage />
      </div>
      {showLesson && selLesson && lessonId && (
        <LessonProgressModal
          lessonId={lessonId}
          open={showLesson}
          onClose={closeModal}
          lesson={selLesson}
          lessonlist={sllLessons?.sllGetGettingStartedLessons ?? []}
          courseMode={MyCourseEnum.Courses}
          course={selMyCourse}
        />
      )}
    </>
  );
}

export default GettingStarted;
